import { Link } from "react-router-dom";
import { useTheme } from "@/context/ThemeContext";

const EFFECTIVE_DATE = "May 13, 2026";

export default function PrivacyPolicy() {
  const { theme } = useTheme();

  return (
    <div
      className="min-h-screen px-4 py-12"
      style={{
        background: theme.appBg || 'linear-gradient(135deg, #0ea5e9, #0f766e)',
        color: theme.textColor,
      }}
    >
      <article className="max-w-3xl mx-auto" style={{ lineHeight: 1.6 }}>
        <header className="mb-10">
          <h1 className="text-4xl font-bold mb-2">EarthSocial Privacy Policy</h1>
          <p style={{ opacity: 0.7, fontSize: '14px' }}>
            Effective {EFFECTIVE_DATE}. This policy describes how EarthSocial handles your
            personal information. Company has its own privacy policy covering the underlying
            Earth Servers identity — this one is just for EarthSocial.
          </p>
        </header>

        <Section title="1. Summary (TL;DR)">
          <ul className="list-disc pl-6 space-y-1">
            <li>
              We store your EarthSocial profile (username, optional display fields, posts,
              comments, messages, marketplace listings) on Earth Servers infrastructure.
            </li>
            <li>
              We read a minimal identity from Company (your stable id, username, discriminator,
              optional display name and avatar reference) so we can tell who you are.
            </li>
            <li>We don't sell your personal information. We don't run ad tracking.</li>
            <li>
              Media uploads (images, video, audio) are encrypted at rest with AES-256-GCM.
            </li>
            <li>
              You can delete your EarthSocial profile from Settings any time; your Company
              account is unaffected.
            </li>
          </ul>
        </Section>

        <Section title="2. What we collect">
          <p>
            <strong>Profile data you give us directly:</strong> EarthSocial username (the one
            you pick when you first sign in), optional profile name, bio, profile picture,
            theme preferences, frame and decoration choices, status message, and notification
            settings.
          </p>
          <p>
            <strong>Content you create on EarthSocial:</strong> posts, comments, replies,
            likes/dislikes, direct messages, community posts and chat, marketplace listings,
            uploaded media, and reports you file. Direct messages are visible to participants
            and to Earth Servers staff when investigating abuse reports or required by law.
          </p>
          <p>
            <strong>Identity mirrored from Company:</strong> your stable Earth Servers user id
            (a ULID), your Company username and discriminator, your Company display name (if
            set), and a reference to your Company avatar. We cache these so we can show
            cross-platform handles on EarthSocial; the data is kept in sync via a server-side
            event subscription. We do not receive your password, email address (except as you
            optionally provide it to us directly), or any other identity data Company holds
            beyond what's listed here.
          </p>
          <p>
            <strong>Technical data we collect automatically:</strong> IP address, user agent
            string, timestamps for sign-ins and API calls, WebSocket connection records (for
            real-time features), error reports if you opt in. We use this for security
            monitoring, rate limiting, and abuse investigation.
          </p>
          <p>
            <strong>Marketplace data:</strong> if you list, buy, or sell on the marketplace,
            we record the transaction details (item, price, parties, timestamps, payment
            provider transaction id). We may need to retain this information for tax,
            accounting, and dispute-resolution purposes even after you delete your profile.
          </p>
        </Section>

        <Section title="3. How we use it">
          <ul className="list-disc pl-6 space-y-1">
            <li>To provide the service — show your posts, deliver your messages, etc.</li>
            <li>
              To personalize your experience — apply your theme, suggest content, surface
              communities you've joined.
            </li>
            <li>
              For safety and moderation — investigate reports, detect abuse, enforce these
              Terms.
            </li>
            <li>
              For security — detect attacks, rate-limit, block compromised accounts.
            </li>
            <li>
              To communicate transactional information — marketplace receipts, security
              notices, policy updates. We don't send marketing emails.
            </li>
            <li>
              To comply with legal obligations and respond to lawful requests from authorities.
            </li>
          </ul>
        </Section>

        <Section title="4. Who we share it with">
          <p>
            <strong>Company:</strong> we exchange your stable user id and minimal profile
            metadata with Company for authentication and identity verification. This is
            essential to the service — without it, we can't tell users apart.
          </p>
          <p>
            <strong>Service providers:</strong> we use third-party infrastructure for hosting,
            CDN, transactional email, and payment processing. These providers process data on
            our behalf under contractual obligations; they don't get to use your data for
            their own purposes.
          </p>
          <p>
            <strong>Law enforcement and legal process:</strong> we may disclose information
            when we believe in good faith that it's required by law, to protect rights or
            safety, or to comply with a valid legal request (subpoena, court order, etc.). We
            aim to push back on overbroad requests and notify affected users where lawfully
            possible.
          </p>
          <p>
            <strong>Other users:</strong> anything you post publicly is, by definition, public.
            Other EarthSocial users will see your username, profile, public posts, community
            activity, and marketplace listings.
          </p>
          <p>
            <strong>We do not sell your personal information.</strong> Full stop.
          </p>
        </Section>

        <Section title="5. How we secure it">
          <ul className="list-disc pl-6 space-y-1">
            <li>
              Connections to EarthSocial are encrypted in transit (HTTPS / WSS).
            </li>
            <li>
              Media uploads (images, video, audio) are encrypted at rest using AES-256-GCM
              with a server-held key.
            </li>
            <li>
              Authentication uses short-lived RS256 JWTs scoped to EarthSocial, refreshed
              silently via a shared parent-domain session cookie. Your password is never
              transmitted to EarthSocial — only to Company.
            </li>
            <li>
              We monitor access to user data and limit it to staff with a legitimate need.
            </li>
          </ul>
          <p>
            No system is perfectly secure; if you suspect a breach affecting your account,
            tell us right away.
          </p>
        </Section>

        <Section title="6. Your rights">
          <p>You have the right to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>Access</strong> the personal information we hold about you.
            </li>
            <li>
              <strong>Correct</strong> inaccurate information — you can edit most fields in
              Settings.
            </li>
            <li>
              <strong>Delete</strong> your EarthSocial profile via Settings. Your Company
              account survives; only your EarthSocial-side data is removed.
            </li>
            <li>
              <strong>Export</strong> your EarthSocial data using "Export Data" in Settings.
              You'll get a JSON file with your profile, posts, comments, follows, and settings.
            </li>
            <li>
              <strong>Opt out</strong> of having an EarthSocial profile at all — when you
              first sign in, you can choose "Skip — keep my Company account only." We won't
              create a row for you and the prompt won't reappear for the rest of that session.
            </li>
          </ul>
          <p>
            If you're in a jurisdiction with additional rights under GDPR, CCPA, or similar
            regimes, those rights apply too. To exercise any of them, contact{" "}
            <a
              href="mailto:[privacy@earthservers.net]"
              className="underline"
              style={{ color: theme.textColor }}
            >
              [privacy@earthservers.net]
            </a>
            . We'll respond within the timeframe required by applicable law.
          </p>
        </Section>

        <Section title="7. Cookies and local storage">
          <p>We use a small number of cookies and browser-storage entries:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <code>company_session</code> — a parent-domain (.earthservers.net) HttpOnly
              cookie set by Company, used by both Company and EarthSocial to recognize you
              across the ecosystem. We don't read its contents; we just use its presence to
              refresh authentication tokens.
            </li>
            <li>
              <code>themeCache</code> and similar entries in <code>sessionStorage</code>{" "}
              and <code>localStorage</code> — your visual preferences (theme, last-viewed
              community) so the app loads with the right look quickly.
            </li>
            <li>
              The EarthSocial JWT itself is held in memory only — not in a cookie or local
              storage — to reduce exposure from cross-site scripting.
            </li>
          </ul>
          <p>We don't use third-party advertising or analytics cookies.</p>
        </Section>

        <Section title="8. Data retention">
          <p>
            We keep your information for as long as your EarthSocial profile is active. When
            you delete your profile:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              Your profile row, posts, comments, likes, and uploaded media are removed
              promptly. Cascading deletes are handled at the database level.
            </li>
            <li>
              Direct messages you sent remain visible to the recipients (we can't unsend);
              your name on those messages is replaced with a tombstone.
            </li>
            <li>
              Marketplace transaction records are retained as long as legally required (tax,
              fraud-prevention, dispute resolution) — typically several years.
            </li>
            <li>
              Operational logs (IP addresses, request traces) are retained for a short window
              for security purposes — typically 30-90 days — and then rotated out.
            </li>
            <li>
              Backups containing your data are retained on a rolling basis (typically 30
              days) and overwritten as new backups are taken.
            </li>
          </ul>
        </Section>

        <Section title="9. Children's privacy">
          <p>
            EarthSocial isn't directed at children under 13, and we don't knowingly collect
            personal information from them. If you believe a child has signed up, contact{" "}
            <a
              href="mailto:[privacy@earthservers.net]"
              className="underline"
              style={{ color: theme.textColor }}
            >
              [privacy@earthservers.net]
            </a>{" "}
            and we'll remove the account.
          </p>
        </Section>

        <Section title="10. International transfers">
          <p>
          EarthSocial is operated from Canada. Our application servers are hosted
          in the United States, while data storage remains in Canada. If you access
          EarthSocial from outside of Canada or the United States, your data may
          be transferred internationally. We rely on standard contractual clauses
          and equivalent protections where applicable.
          </p>
        </Section>

        <Section title="11. Changes to this policy">
          <p>
            We may update this Privacy Policy occasionally. For material changes that expand
            what we collect or how we use it, we'll notify you in advance (in-app notice or
            email) and update the "Effective" date at the top.
          </p>
        </Section>

        <Section title="12. Contact us">
          <p>
            Privacy questions or requests:{" "}
            <a
              href="mailto:[privacy@earthservers.net]"
              className="underline"
              style={{ color: theme.textColor }}
            >
              [privacy@earthservers.net]
            </a>
            . General contact:{" "}
            <a
              href="mailto:earthserversnet@gmail.com"
              className="underline"
              style={{ color: theme.textColor }}
            >
              earthserversnet@gmail.com
            </a>
            .
          </p>
        </Section>

        <footer className="mt-12 pt-6" style={{ borderTop: `1px solid ${theme.textColor}22` }}>
          <Link
            to="/login"
            className="inline-block px-6 py-3 rounded-lg font-semibold"
            style={{
              backgroundColor: theme.accentColor,
              color: '#1a1a1a',
            }}
          >
            Back to Login
          </Link>
        </footer>
      </article>
    </div>
  );
}

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

function Section({ title, children }: SectionProps) {
  return (
    <section className="mb-8">
      <h2 className="text-2xl font-semibold mb-3">{title}</h2>
      <div className="space-y-3" style={{ opacity: 0.92 }}>
        {children}
      </div>
    </section>
  );
}
